import React, { useState, useCallback, useEffect } from 'react';
import CommonSelect from '../../common/select/CommonSelect';
import InputWithLabel from '../../common/InputWithLabel';
import TextareaWithLabel from '../../common/TextareaWithLabel';
import LazyLoadImageProp from '../../common/LazyLoadImage';
import DragDropFileInput from '../../common/DragDropFileInput';
import { XMarkIcon } from '@heroicons/react/20/solid';
import Icons from '../../svgs/icons';
import { useWorkoutCreation } from '../../../context/WorkoutCreationContext';
import { errorToast, successToast } from '../../../utils/helper';
import { getExerciseCategories } from '../../../api/healthAPI';

// Enums from Backend Constant.js - WORKOUT_DIFFICULTY
const DIFFICULTY_LEVELS = [
  { label: 'Beginner', value: 'Beginner' },
  { label: 'Intermediate', value: 'Intermediate' },
  { label: 'Advanced', value: 'Advanced' },
  { label: 'Newbie', value: 'Newbie' },
  { label: 'Moderate', value: 'Moderate' },
  { label: 'Athlete', value: 'Athlete' },
  { label: 'Disability Exercises', value: 'Disability Exercises' }
];

// Enums from Backend Constant.js - WORKOUT_LOCATION
const LOCATIONS = [
  { label: 'Home', value: 'Home' },
  { label: 'Gym', value: 'Gym' },
  { label: 'Outdoor', value: 'Outdoor' }
];

// Enums from Backend Constant.js - WORKOUT_MUSCLE_GROUP
const WorkoutDetailsTab = ({ isView }) => {
  // Use context only
  const { workoutData, errors, updateWorkoutData, validateStep1, setErrors } = useWorkoutCreation();

  // Local state for thumbnail preview
  const [previewthumbnailImage, setPreviewthumbnailImage] = useState('');
  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [userUploadedNewImage, setUserUploadedNewImage] = useState(false);

  // Local state for workout categories
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // Fetch categories on component mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoadingCategories(true);
        const response = await getExerciseCategories();
        const categoryList = response?.data || [];
        const formattedCategories = categoryList.map((category) => ({
          label: category?.name || category?.title || '',
          value: category?._id || category?.id || ''
        }));
        setCategories(formattedCategories);
      } catch (error) {
        console.error('Error fetching categories:', error);
        errorToast('Failed to load workout categories');
      } finally {
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  // Update preview when workoutData thumbnail_url changes (only if user hasn't uploaded new image)
  React.useEffect(() => {
    if (workoutData.thumbnail_url && !userUploadedNewImage) {
      setPreviewthumbnailImage(workoutData.thumbnail_url);
    }
  }, [workoutData.thumbnail_url, userUploadedNewImage]);

  // Handle save and continue
  const handleSaveAndContinue = useCallback(async () => {
    const { errors: validationErrors, isValid } = validateStep1(workoutData);
    if (!isValid) {
      setErrors(validationErrors);
      errorToast('Please fix the validation errors');
      return;
    }

    updateWorkoutData({ currentStep: 2 });
  }, [validateStep1, workoutData, setErrors, updateWorkoutData]);

  // Handle form field changes
  const handleChangeInternal = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      const fieldValue = type === 'checkbox' ? checked : value;

      updateWorkoutData({
        [name]: fieldValue
      });
    },
    [updateWorkoutData]
  );

  // Handle select field changes
  const handleSelectChangeInternal = useCallback(
    (field, selectedOption) => {
      let value;
      if (field === 'muscle_groups' || field === 'category_ids') {
        value = Array.isArray(selectedOption)
          ? selectedOption.map((option) => option.value)
          : selectedOption
          ? [selectedOption.value]
          : [];
      } else {
        value = selectedOption?.value || '';
      }
      updateWorkoutData({
        [field]: value
      });
    },
    [updateWorkoutData]
  );

  // Handle thumbnail upload
  const handleThumbnailUpload = useCallback(
    async (file) => {
      if (!file) return;

      if (!/\.(jpg|jpeg|png|gif)$/i.test(file.name)) {
        errorToast('Please upload a valid image file (JPG, JPEG, PNG, GIF)');
        return;
      }

      if (file.size > 1024 * 1024) {
        errorToast('Image file size should be less than 1MB');
        return;
      }

      try {
        setUploadingThumbnail(true);
        const localPreview = URL.createObjectURL(file);
        setPreviewthumbnailImage(localPreview);
        setUserUploadedNewImage(true); // Mark that user uploaded a new image

        // Store file locally, will upload via presigned URL from API response
        updateWorkoutData({
          thumbnailFile: file,
          thumbnail_url: localPreview
        });

        successToast('Thumbnail selected successfully');
      } catch (error) {
        console.error('Thumbnail selection error:', error);
        errorToast('Failed to select thumbnail');
        updateWorkoutData({
          thumbnailFile: null,
          thumbnail_url: ''
        });
        setPreviewthumbnailImage('');
        setUserUploadedNewImage(false);
      } finally {
        setUploadingThumbnail(false);
      }
    },
    [updateWorkoutData]
  );

  return (
    <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Workout Details</h3>

      <div className="grid lg:gap-6 gap-4 md:grid-cols-2 grid-cols-1">
        {/* Workout Title */}
        <div>
          <InputWithLabel
            label="Workout Title"
            placeholder="Enter workout title: e.g Full Body Strength Training"
            onChange={handleChangeInternal}
            errorMargin="mt-1"
            labelMargin="mb-1"
            errorSize="text-[12px]"
            isLengthValidate={true}
            limit={100}
            labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
            value={workoutData.workout_name}
            name="workout_name"
            id="workout_name"
            error={errors?.workout_name}
            isRequired={true}
            disabled={isView}
          />
        </div>

        {/* Difficulty Level */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Difficulty <span className="text-red-400">&#42;</span>
          </label>
          <CommonSelect
            disabled={isView}
            name="difficulty"
            onChange={(e) => handleSelectChangeInternal('difficulty', e)}
            value={DIFFICULTY_LEVELS?.find((option) => option?.value === workoutData.difficulty)}
            options={DIFFICULTY_LEVELS}
            placeholder="Level of difficulty"
          />
          {errors?.difficulty && (
            <span className="text-red-500 text-[12px] mt-1 block">{errors.difficulty}</span>
          )}
        </div>

        {/* Workout Categories */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Workout Categories
          </label>
          <CommonSelect
            disabled={isView || loadingCategories}
            name="category_ids"
            isMulti={true}
            onChange={(e) => handleSelectChangeInternal('category_ids', e)}
            value={
              Array.isArray(workoutData.category_ids)
                ? workoutData.category_ids
                    .map((catId) => categories?.find((option) => option?.value === catId))
                    .filter(Boolean)
                : []
            }
            options={categories}
            placeholder={loadingCategories ? 'Loading categories...' : 'Select workout categories'}
            isLoading={loadingCategories}
          />
          {errors?.category_ids && (
            <span className="text-red-500 text-[12px] mt-1 block">{errors.category_ids}</span>
          )}
        </div>

        {/* Thumbnail Upload */}
        {!isView && (
          <div className="md:col-span-2">
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Thumbnail (Upload 512px * 512px image) (Max image size 1MB)
            </label>
            <div className="mt-1">
              {previewthumbnailImage ? (
                <>
                  <div className="relative">
                    <div className="h-10 flex items-center justify-between gap-3 w-full rounded-lg px-4 py-[8px] text-sm text-shoorah-lightBlack dark:border-shoorah-darkBorder dark:text-shoorah-darkBg appearance-none placeholder-gray-400 bg-shoorah-normanGray dark:bg-shoorah-darkBgColor border-[1.5px] border-shoorah-borderGray">
                      <p className="truncate mr-6">
                        {previewthumbnailImage?.split('/')[
                          previewthumbnailImage?.split('/')?.length - 1
                        ] || 'Thumbnail uploaded'}
                      </p>
                    </div>
                    <XMarkIcon
                      className="absolute top-1/2 transform -translate-y-1/2 right-[10px] w-[20px] text-red-500 cursor-pointer"
                      onClick={() => {
                        setPreviewthumbnailImage('');
                        setUserUploadedNewImage(false); // Reset flag when removing image
                        updateWorkoutData({ thumbnail_url: '', thumbnailFile: null });
                      }}
                    />
                  </div>
                  <div className="mx-auto border rounded-lg mt-2 text-center">
                    <LazyLoadImageProp
                      imageSrc={previewthumbnailImage}
                      className="mx-auto max-h-[250px] rounded-lg"
                    />
                  </div>
                </>
              ) : (
                <DragDropFileInput
                  accept=".jpg,.jpeg,.png,.gif"
                  hint="Supports: JPG, JPEG, PNG, GIF (Max 1MB)"
                  icon={<Icons.videoUploadIcon className="mb-5 text-shoorah-primary" />}
                  onChange={handleThumbnailUpload}
                  disabled={uploadingThumbnail}
                  uploading={uploadingThumbnail}
                  error={errors?.thumbnail_url}
                />
              )}
            </div>
          </div>
        )}
        {/* Workout Overview - Full Width */}
        <div className="md:col-span-2">
          <TextareaWithLabel
            label="Workout Description"
            placeholder="Enter overview: e.g A comprehensive full-body workout focusing on strength and endurance"
            onChange={handleChangeInternal}
            errorMargin="mt-1"
            labelMargin="mb-1"
            errorSize="text-[12px]"
            className="min-h-[120px]"
            isLengthValidate={true}
            limit={500}
            labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
            value={workoutData.workout_overview}
            name="workout_overview"
            id="workout_overview"
            error={errors?.workout_overview}
            isRequired={true}
            disabled={isView}
          />
        </div>

        {/* Workout Location */}
        <div>
          <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
            Workout Location <span className="text-red-400">&#42;</span>
          </label>
          <CommonSelect
            disabled={isView}
            name="location"
            onChange={(e) => handleSelectChangeInternal('location', e)}
            value={LOCATIONS?.find((option) => option?.value === workoutData.location)}
            options={LOCATIONS}
            placeholder="Location"
          />
          {errors?.location && (
            <span className="text-red-500 text-[12px] mt-1 block">{errors.location}</span>
          )}
        </div>
      </div>

      {/* Save and Continue Button */}
      {!isView && (
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSaveAndContinue}
            className="btn-primary"
          >
            Save and Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default WorkoutDetailsTab;
export { DIFFICULTY_LEVELS, LOCATIONS };
